import { Dialog } from "layout";
import React, { useCallback, useEffect, useState } from "react";
import { useAppContext } from "context";
import { lsGet } from "hooks";
import Body from "./Body";
import { REQUIRED, SWITCH } from "./modes";

// Shown once after login to answer "which company and which branch am I working in?",
// and re-openable at any time from the branch chip in the top bar — openBizContext()
// on AppContext. Before that chip existed the only way to change branch was to clear
// localStorage, which stranded anyone who never opens a till.
//
// layout/Dialog is keepMounted, so Body mounts here at app start and stays mounted for
// the life of the session. Everything that must happen on a RE-open is keyed on `mode`
// inside Body, never on mount.

const hasStoredContext = () => !!lsGet("bizContext")?.outletId;

const BizContext = () => {
  const { bizPrompt, setBizPrompt } = useAppContext();

  // `open` drives the transition; `mode` drives what is RENDERED. They are separate
  // because they have different lifetimes: `open` goes false the instant Cancel is
  // clicked, while `mode` has to survive the whole 400ms Grow exit or the dialog
  // visibly re-renders as the other variant on its way out. That is exactly the bug
  // this split fixes — Cancel used to flash the red LOGOUT chip while animating away,
  // because the button was derived from the same flag that closed the dialog.
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null);

  // bizPrompt is an EVENT ("open it now"), not a mode. Consume it on arrival and
  // reset it, so nothing downstream can read it as state and inherit its lifetime.
  //
  // The mode is resolved from what is actually in storage rather than from which
  // button was pressed: someone can reach the chip with no stored branch at all (a
  // second tab cleared it), and offering that person Cancel would close the dialog
  // onto an app with no branch.
  useEffect(() => {
    if (!bizPrompt) return;
    setMode(hasStoredContext() ? SWITCH : REQUIRED);
    setOpen(true);
    setBizPrompt(false);
  }, [bizPrompt, setBizPrompt]);

  // Body's one-shot mount check calls this when it finds nothing cached.
  const require = useCallback(() => {
    setMode(REQUIRED);
    setOpen(true);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  return (
    // No setShow and no onClose: layout/Dialog renders its X only when handed setShow,
    // and that same prop is what lets the backdrop and ESC close the dialog. In REQUIRED
    // there is no branch to fall back to, so none of them may close it — the header's
    // own Cancel / Logout button is the single, explicit way out on both paths.
    <Dialog
      show={open}
      maxWidth="xs"
      TransitionProps={{ onExited: () => setMode(null) }}
    >
      <Body {...{ mode, onRequire: require, onClose: close }} />
    </Dialog>
  );
};

export default BizContext;
